import moment from 'moment';
import { IDateTimeParams } from 'modules/params/param.type';

export const DEFAULT_DATE_FORMAT = 'MM/DD/YYYY';
export const DEFAULT_TIME_FORMAT = 'HH:MM XM';

export type DateTimeInput = string | number | Date | null | undefined;

export interface ResolvedDateTimeFormats {
  dateFormat: string;
  timeFormat: string;
  momentDate: string;
  momentTime: string;
}

/** UI time labels → moment tokens (MM is month in moment; minutes are mm). */
export const toMomentTimeFormat = (uiFormat: string): string => {
  if (uiFormat === 'HH:MM') return 'HH:mm';
  if (uiFormat === 'HH:MM XM') return 'hh:mm A';
  return 'hh:mm A';
};

export const toMomentDateFormat = (uiFormat: string): string => {
  if (!uiFormat) return DEFAULT_DATE_FORMAT;
  return uiFormat;
};

const asSettingObject = (setting: any): Partial<IDateTimeParams> | null => {
  if (!setting || typeof setting !== 'object') return null;
  // Immutable Map from redux
  if (typeof setting.toJS === 'function') {
    return setting.toJS();
  }
  return setting;
};

/**
 * When is_default is true, missing setting, or both formats empty → app defaults.
 * Custom formats apply only when is_default is explicitly false and formats are set.
 */
export const resolveFormats = (setting?: any): ResolvedDateTimeFormats => {
  const obj = asSettingObject(setting);
  const isAutomatic = !obj || obj.is_default !== false;
  const hasCustomFormats = !!(obj?.date_format || obj?.time_format);
  const useDefault = isAutomatic || !hasCustomFormats;

  const dateFormat =
    useDefault || !obj?.date_format ? DEFAULT_DATE_FORMAT : obj.date_format;
  const timeFormat =
    useDefault || !obj?.time_format ? DEFAULT_TIME_FORMAT : obj.time_format;

  return {
    dateFormat,
    timeFormat,
    momentDate: toMomentDateFormat(dateFormat),
    momentTime: toMomentTimeFormat(timeFormat),
  };
};

const toMoment = (input: DateTimeInput) => {
  if (input === null || input === undefined || input === '') return null;
  const m = moment(input);
  return m.isValid() ? m : null;
};

export const formatUserDate = (input: DateTimeInput, setting?: any): string => {
  const m = toMoment(input);
  if (!m) return '';
  const { momentDate } = resolveFormats(setting);
  return m.format(momentDate);
};

export const formatUserTime = (input: DateTimeInput, setting?: any): string => {
  const m = toMoment(input);
  if (!m) return '';
  const { momentTime } = resolveFormats(setting);
  return m.format(momentTime);
};

export const formatUserDateTime = (input: DateTimeInput, setting?: any): string => {
  const m = toMoment(input);
  if (!m) return '';
  const { momentDate, momentTime } = resolveFormats(setting);
  return `${m.format(momentTime)} ${m.format(momentDate)}`;
};

/** Module cache so non-React helpers (getCurrentDate) can use the latest setting. */
let cachedSetting: any = null;

export const setCachedDateTimeSetting = (setting: any) => {
  cachedSetting = setting;
};

export const getCachedDateTimeSetting = () => cachedSetting;

export const formatUserDateCached = (input: DateTimeInput): string =>
  formatUserDate(input, cachedSetting);

export const formatUserTimeCached = (input: DateTimeInput): string =>
  formatUserTime(input, cachedSetting);

export const formatUserDateTimeCached = (input: DateTimeInput): string =>
  formatUserDateTime(input, cachedSetting);

/** Prefer publishedAt + user date format; fall back to API display string. */
export const formatArticleDisplayDate = (
  article: { date?: string; publishedAt?: string } | null | undefined,
  setting?: any
): string => {
  if (!article) return '';
  if (article.publishedAt) {
    const formatted = formatUserDate(article.publishedAt, setting ?? cachedSetting);
    if (formatted) return formatted;
  }
  return article.date || '';
};
